import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService from '../services/authService';
import { setUnauthorizedHandler } from '../services/apiClient';
import useIdleTimeout from '../hooks/useIdleTimeout';

const AuthContext = createContext(null);

const SESSION_KEY = 'idh.session';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous
  // Why the session ended, when it was not the person's own doing.
  const [signedOutReason, setSignedOutReason] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await authService.me();
      setSession(data);
      setStatus('authenticated');
      return data;
    } catch {
      setSession(null);
      setStatus('anonymous');
      return null;
    }
  }, []);

  useEffect(() => {
    // A "session" marker is remembered locally so a reload keeps you signed in; the
    // access token itself is never stored, only held in memory.
    const remembered = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (!remembered) {
      setStatus('anonymous');
      return;
    }
    // Exchange the refresh cookie for an access token first, so the first screen's
    // requests do not all 401-then-retry.
    authService.bootstrap().then((ok) => {
      if (ok) load();
      else {
        setSession(null);
        setStatus('anonymous');
      }
    });
  }, [load]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession(null);
      setStatus('anonymous');
      sessionStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SESSION_KEY);
    });
  }, []);

  const login = useCallback(
    async (credentials) => {
      const result = await authService.login(credentials);
      if (result.mfaRequired) return result;
      const store = credentials.remember ? localStorage : sessionStorage;
      store.setItem(SESSION_KEY, '1');
      await load();
      return result;
    },
    [load],
  );

  const verifyMfa = useCallback(
    async (payload) => {
      const result = await authService.verifyMfa(payload);
      const store = payload.remember ? localStorage : sessionStorage;
      store.setItem(SESSION_KEY, '1');
      await load();
      // `recoveryCodes` is present only just after enrolment — the caller shows them once.
      return result;
    },
    [load],
  );

  /**
   * Sign out after the organisation's idle period.
   *
   * Only while signed in, and only when a limit is configured. `signedOutReason` survives the
   * sign-out so the login screen can say what happened — somebody who returns to a login page
   * they did not ask for should not have to guess whether they were logged out or crashed.
   */
  useIdleTimeout(status === 'authenticated' ? session?.sessionIdleMinutes : 0, () => {
    setSignedOutReason('idle');
    // Best effort: the refresh token is revoked here so the session cannot be resumed, but
    // the screen clears whether or not that request reaches the server.
    authService.logout().catch(() => {});
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setStatus('anonymous');
  });

  const logout = useCallback(async () => {
    setSignedOutReason(null);
    await authService.logout();
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setStatus('anonymous');
  }, []);

  const switchUser = useCallback(async (userId) => {
    const data = await authService.switchUser(userId);
    setSession(data);
    return data;
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const user = await authService.updateProfile(patch);
    setSession((prev) => (prev ? { ...prev, user } : prev));
    return user;
  }, []);

  const value = useMemo(() => {
    const permissions = new Set(session?.permissions || []);
    return {
      status,
      session,
      user: session?.user || null,
      role: session?.roles?.[0] || null,
      permissions,
      features: session?.features || {},
      /** Driver and region only — where documents are kept. The bucket name is admin-only. */
      storage: session?.storage || {},
      /**
       * Whether to offer pinning anywhere in the interface. The server decides, because the
       * rule combines a permission key with a per-department admin flag that never reaches
       * `permissions` — `can('ADMIN_DEPARTMENT_MANAGE')` would quietly hide the action from
       * every department manager who holds only the flag.
       */
      canPin: Boolean(session?.canPin),
      /** 'idle' when the session timed out, so the sign-in screen can explain itself. */
      signedOutReason,
      clearSignedOutReason: () => setSignedOutReason(null),
      organization: session?.organization || null,
      /** UX-level check only — the API enforces the real one on every request. */
      can: (key) => (Array.isArray(key) ? key.some((k) => permissions.has(k)) : permissions.has(key)),
      canAll: (keys) => keys.every((k) => permissions.has(k)),
      login,
      verifyMfa,
      logout,
      switchUser,
      updateProfile,
      reload: load,
    };
  }, [session, status, signedOutReason, login, verifyMfa, logout, switchUser, updateProfile, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export default AuthContext;
